import { useState, useEffect, useRef, lazy, Suspense } from "react";
import axios from "axios";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import OfflineStatusPill from "./components/OfflineStatusPill";
import LegalAcceptanceGate from "./components/LegalAcceptanceGate";
import OfflineQueueBanner from "./components/OfflineQueueBanner";
import { setErrorTrackingUser } from "./services/errorTracking";

const PlatformAdminDashboard = lazy(() => import("./pages/PlatformAdminDashboard"));

function AppContent({ token, user, onLogout, initialEquipmentId }) {
  return (
    <>
      <OfflineQueueBanner token={token} />
      <Dashboard
        user={user}
        token={token}
        onLogout={onLogout}
        initialEquipmentId={initialEquipmentId}
      />
      <OfflineStatusPill />
    </>
  );
}

function App() {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [legalStatus, setLegalStatus] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  // Error reports carry only the user id + organization id (no-op unless
  // error tracking is configured — see services/errorTracking.js).
  useEffect(() => {
    setErrorTrackingUser(user);
  }, [user]);

  const path = window.location.pathname;
  // Equipment QR codes encode a direct link to /equipment/:id. There's no
  // router in this app — Dashboard's equipment view is plain React state —
  // so this is the one place that URL gets turned into that state. Kept
  // across the login screen if the scanner isn't authenticated yet: `path`
  // doesn't change, so this re-derives the same id once `token` becomes
  // truthy and AppContent/Dashboard actually mount.
  const equipmentMatch = path.match(/^\/equipment\/([^/]+)\/?$/);
  const initialEquipmentId = equipmentMatch ? equipmentMatch[1] : null;

  // A brand-new user's legal fields are null, so `legal.needsLegalAcceptance`
  // comes back true just like an existing pre-feature user's does, landing
  // on the gate the same way regardless of how the account was created.
  function handleLogin(newToken, userData, legal) {
    setSessionExpired(false);
    setToken(newToken);
    setUser(userData);
    setLegalStatus(legal || null);
  }

  function handleLogout() {
    setToken(null);
    setUser(null);
    setLegalStatus(null);
  }

  // Called by LegalAcceptanceGate once POST /api/legal/accept succeeds —
  // swaps in the fresh user/legal status so the gate unmounts and
  // AppContent renders normally, no re-login required.
  function handleLegalAccepted({ user: updatedUser, legal }) {
    if (updatedUser) setUser(updatedUser);
    setLegalStatus(legal || null);
  }

  // No page fetches its own token refresh, so a token that expires mid-session
  // (or is otherwise rejected) previously just left whichever page happened to
  // make the next request stuck showing the raw backend error. Catch it once,
  // globally, and drop back to Login with a clear reason instead.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        // The platform 2FA code endpoints are excluded: a mistyped code is
        // bad input on a valid session, not an expired one (the server
        // answers 400 for it — this guard is defense in depth).
        const url = err.config?.url || "";
        const is2faCodeCheck = /\/api\/platform\/2fa\/verify(-setup)?$/.test(url);
        if (err.response?.status === 401 && tokenRef.current && !is2faCodeCheck) {
          setSessionExpired(true);
          handleLogout();
        }
        return Promise.reject(err);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  if (path === "/forgot-password") return <ForgotPassword />;
  if (path.startsWith("/reset-password/")) return <ResetPassword />;
  // Public self-registration is closed — accounts now come from platform
  // provisioning or an org admin adding a teammate. Old/bookmarked
  // /register links get redirected rather than 404ing.
  if (path === "/register") {
    window.location.replace("/login");
    return null;
  }

  if (!token) {
    return <Login onLogin={handleLogin} sessionExpired={sessionExpired} />;
  }

  // Reachable only by typing this URL directly — deliberately not in
  // Dashboard's nav (see PlatformAdminDashboard.js). Checked before the
  // legal-acceptance gate below on purpose: this is Mitchell's own
  // operational-emergency tooling, gated by its own much stronger 2FA
  // check inside the component itself, and it shouldn't be blockable by
  // his own account's ordinary Terms/Privacy re-acceptance status.
  if (path === "/platform-admin") {
    return (
      <Suspense fallback={null}>
        <PlatformAdminDashboard token={token} onLogout={handleLogout} />
      </Suspense>
    );
  }

  // Blocks everything behind it — Dashboard/AppContent never mounts until
  // the current Terms/Privacy versions are accepted. Applies identically
  // to a brand-new registrant and an existing user whose acceptedVersion
  // predates this feature (both come back with needsLegalAcceptance: true).
  if (legalStatus?.needsLegalAcceptance) {
    return (
      <LegalAcceptanceGate
        token={token}
        onAccepted={handleLegalAccepted}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <AppContent
      token={token}
      user={user}
      onLogout={handleLogout}
      initialEquipmentId={initialEquipmentId}
    />
  );
}

export default App;
